import { ComparisonTimelineStep } from "../types/timeline";

/**
 * Static timeline configuration for comparison/validation processes
 * Based on ComparisonEnum from backend
 *
 * Each step includes:
 * - id: Sequential step number
 * - log_enum_key: The enum key from backend
 * - title: User-friendly step title
 * - description: Step description/message
 * - is_visible_on_ui: Whether to show this step in the UI
 */
export const VALIDATION_TIMELINE_STEPS: ComparisonTimelineStep[] = [
  {
    id: 1,
    log_enum_key: "VALIDATION_STARTED_validation",
    title: "Validation process started.",
    displayMessage: "Validation process started.",
    is_visible_on_ui: true,
  },
  {
    id: 2,
    log_enum_key: "PREPARE_REQUEST_validation",
    title: "Prepare validation request context and parameters completed.",
    displayMessage: "Prepared validation request context and parameters.",
    is_visible_on_ui: true,
  },
  {
    id: 3,
    log_enum_key: "INSERT_SYSTEM_MAPPINGS_validation",
    title: "Insert system mappings to the database completed.",
    displayMessage: "Inserted system mappings into the database.",
    is_visible_on_ui: true,
  },
  {
    id: 4,
    log_enum_key: "PREPARE_DATA_SERVICE_validation",
    title: "Preparing data preparation service.",
    displayMessage: "Preparing the data preparation service.",
    is_visible_on_ui: true,
  },
  {
    id: 5,
    log_enum_key: "LOAD_COLUMN_DEFS_validation",
    title: "Load resource column definitions and datatypes completed.",
    displayMessage: "Loaded resource column definitions and data types.",
    is_visible_on_ui: true,
  },
  {
    id: 6,
    log_enum_key: "FETCH_RESOURCE_DATA_validation",
    title: "Fetch given resource data completed.",
    displayMessage: "Fetched the given resource data.",
    is_visible_on_ui: true,
  },
  {
    id: 7,
    log_enum_key: "PREPARE_BASE_RESOURCE_validation",
    title: "Prepare resource for base resource completed.",
    displayMessage: "Prepare resource for base resource completed.",
    is_visible_on_ui: false,
  },
  {
    id: 8,
    log_enum_key: "PREPARE_BASE_RESC_REQUEST_validation",
    title:
      "Preparing request context and parameters for aggregation base resource completed.",
    displayMessage:
      "Preparing request context and parameters for aggregation base resource completed.",
    is_visible_on_ui: false,
  },
  {
    id: 9,
    log_enum_key: "VALIDATE_BASE_RESOURCE_validation",
    title: "Validation completed for base resource.",
    displayMessage: "Validation completed for base resource.",
    is_visible_on_ui: false,
  },
  {
    id: 10,
    log_enum_key: "FETCH_AGGREGATED_DATA_validation",
    title: "Fetch aggregated resource data completed.",
    displayMessage: "Fetched aggregated resource data.",
    is_visible_on_ui: true,
  },
  {
    id: 11,
    log_enum_key: "EXECUTE_AGGREGATION_QUERY_validation",
    title: "Executing aggregation query for resource completed.",
    displayMessage: "Executing aggregation query for resource completed.",
    is_visible_on_ui: false,
  },
  {
    id: 12,
    log_enum_key: "FETCH_ADDN_BEFORE_DERIVED_STARTED_validation",
    title:
      "Fetch additional resource data before generating derived columns started for resource {resource_id}.",
    displayMessage:
      "Started fetching additional resource data before generating derived columns for resource {resource_id}.",
    is_visible_on_ui: true,
  },
  {
    id: 13,
    log_enum_key: "FETCH_BEFORE_DERIVED_validation",
    title:
      "Fetch additional resource data before generating derived columns completed.",
    displayMessage:
      "Fetched additional resource data before generating derived columns.",
    is_visible_on_ui: true,
  },
  {
    id: 14,
    log_enum_key: "GENERATE_DERIVED_validation",
    title: "Generate derived columns completed.",
    displayMessage: "Generated derived columns.",
    is_visible_on_ui: true,
  },
  {
    id: 15,
    log_enum_key: "FETCH_ADDN_AFTER_DERIVED_STARTED_validation",
    title:
      "Fetch additional resource data after generating derived columns started for resource {resource_id}.",
    displayMessage:
      "Started fetching additional resource data after generating derived columns for resource {resource_id}.",
    is_visible_on_ui: true,
  },
  {
    id: 16,
    log_enum_key: "FETCH_AFTER_DERIVED_validation",
    title:
      "Fetch additional resource data after generating derived columns completed.",
    displayMessage:
      "Fetched additional resource data after generating derived columns.",
    is_visible_on_ui: true,
  },
  {
    id: 17,
    log_enum_key: "PREPARE_ADDN_RESOURCE_STARTED_validation",
    title: "Prepare resource for additional resource {resource_id} started.",
    displayMessage:
      "Prepare resource for additional resource {resource_id} started.",
    is_visible_on_ui: false,
  },
  {
    id: 18,
    log_enum_key: "PREPARE_ADDN_RESOURCE_validation",
    title: "Prepare resource for additional resource completed.",
    displayMessage: "Prepare resource for additional resource completed.",
    is_visible_on_ui: false,
  },
  {
    id: 19,
    log_enum_key: "EXECUTE_ADDN_RESOURCE_QUERY_validation",
    title: "Preparing and executing query of additional resource completed.",
    displayMessage:
      "Preparing and executing query of additional resource completed.",
    is_visible_on_ui: false,
  },
  {
    id: 20,
    log_enum_key: "REMOVE_NULL_COLUMNS_validation",
    title: "Drop null columns completed.",
    displayMessage: "Dropped null columns.",
    is_visible_on_ui: true,
  },
  {
    id: 21,
    log_enum_key: "APPLY_FILTER_RULES_validation",
    title: "Apply resource-specific filter rules completed.",
    displayMessage: "Applied resource-specific filter rules.",
    is_visible_on_ui: true,
  },
  {
    id: 22,
    log_enum_key: "REMOVE_NULL_UNIQUE_KEYS_validation",
    title: "Remove rows with null values in unique keys completed.",
    displayMessage: "Removed rows with null values in unique keys.",
    is_visible_on_ui: true,
  },
  {
    id: 23,
    log_enum_key: "INIT_SEVERITY_MAPPINGS_validation",
    title: "Initialize validation severity mappings.",
    displayMessage: "Initialized validation severity mappings.",
    is_visible_on_ui: true,
  },
  {
    id: 24,
    log_enum_key: "SAMPLE_DATA_validation",
    title: "Sample data if sampling is enabled.",
    displayMessage: "Sampled data if sampling is enabled.",
    is_visible_on_ui: true,
  },
  {
    id: 25,
    log_enum_key: "PROCESS_DUPLICATES_validation",
    title: "Identify and process duplicate records.",
    displayMessage: "Identified and processed duplicate records.",
    is_visible_on_ui: true,
  },
  {
    id: 26,
    log_enum_key: "EXECUTE_CROSS_FIELD_RULES_validation",
    title: "Execute cross-field validation rules completed.",
    displayMessage: "Executed cross-field validation rules.",
    is_visible_on_ui: true,
  },
  {
    id: 27,
    log_enum_key: "MAP_TO_SEVERITY_validation",
    title: "Map columns to severity levels completed.",
    displayMessage: "Mapped columns to severity levels.",
    is_visible_on_ui: true,
  },
  {
    id: 28,
    log_enum_key: "CREATE_VALIDATION_SCHEMAS_validation",
    title: "Create validation schemas for columns completed.",
    displayMessage: "Created validation schemas for columns.",
    is_visible_on_ui: true,
  },
  {
    id: 29,
    log_enum_key: "EXECUTE_GENERAL_RULES_validation",
    title: "Execute general validation rules completed.",
    displayMessage: "Executed general validation rules.",
    is_visible_on_ui: true,
  },
  {
    id: 30,
    log_enum_key: "EXECUTE_CUSTOM_RULES_validation",
    title: "Execute custom validation rules completed.",
    displayMessage: "Executed custom validation rules.",
    is_visible_on_ui: true,
  },
  {
    id: 31,
    log_enum_key: "CONSOLIDATE_ERRORS_validation",
    title: "Consolidate all validation errors.",
    displayMessage: "Consolidated all validation errors.",
    is_visible_on_ui: true,
  },
  {
    id: 32,
    log_enum_key: "STORE_ERRORS_validation",
    title: "Store validation errors in database completed.",
    displayMessage: "Stored validation errors in the database.",
    is_visible_on_ui: true,
  },
  {
    id: 33,
    log_enum_key: "GENERATE_REPORTS_validation",
    title:
      "Generate CSV/Excel validation reports and detailed validation summary completed.",
    displayMessage:
      "Generated CSV/Excel validation reports and a detailed validation summary.",
    is_visible_on_ui: true,
  },
  {
    id: 34,
    log_enum_key: "GENERATE_RESPONSE_validation",
    title: "Generate comprehensive validation response.",
    displayMessage: "Generated a comprehensive validation response.",
    is_visible_on_ui: true,
  },
  {
    id: 35,
    log_enum_key: "PREPARE_RESULT_OBJECT_validation",
    title: "Prepare final ValidationResult object.",
    displayMessage: "Prepared the final ValidationResult object.",
    is_visible_on_ui: true,
  },
  {
    id: 36,
    log_enum_key: "RESULT_UPDATE_validation",
    title: "Update and save validation result.",
    displayMessage: "Updated and saved the validation result.",
    is_visible_on_ui: true,
  },
  {
    id: 37,
    log_enum_key: "VALIDATION_COMPLETED_validation",
    title: "Validation completed.",
    displayMessage: "Validation completed.",
    is_visible_on_ui: true,
  },
  {
    id: 38,
    log_enum_key: "CLEANUP_RESOURCES_validation",
    title: "Clean up resources and memory.",
    displayMessage: "Cleaned up resources and memory.",
    is_visible_on_ui: true,
  },
];

/**
 * Validation timeline steps as rendered on the Validation History page.
 * Excludes the internal child steps marked is_visible_on_ui: false (DQ-8832).
 * Entries stay in VALIDATION_TIMELINE_STEPS so hidden keys remain resolvable.
 * Defined at module level so the array reference stays stable across renders.
 */
export const VALIDATION_HISTORY_TIMELINE_STEPS: ComparisonTimelineStep[] =
  VALIDATION_TIMELINE_STEPS.filter((step) => step.is_visible_on_ui);

export const COMPARISON_TIMELINE_STEPS: ComparisonTimelineStep[] = [
  {
    id: 1,
    log_enum_key: "COMPARISON_STARTED_comparison",
    title: "Rule execution started.",
    displayMessage: "Rule execution started.",
    is_visible_on_ui: true,
  },
  {
    id: 2,
    log_enum_key: "PREPARE_CONTEXT_comparison",
    title: "Scan rule request and initialize context data completed.",
    displayMessage: "Scanned rule request and initialized context data.",
    is_visible_on_ui: true,
  },
  {
    id: 3,
    log_enum_key: "VALIDATE_SYSTEM_ACCESS_comparison",
    title:
      "Validate user system access and insert system mappings in db completed.",
    displayMessage:
      "Validated user system access and inserted system mappings in the database.",
    is_visible_on_ui: true,
  },
  {
    id: 4,
    log_enum_key: "PREPARE_RULE_PAYLOAD_comparison",
    title: "Prepare rule payload with all details completed.",
    displayMessage: "Prepared rule payload with all details.",
    is_visible_on_ui: true,
  },
  {
    id: 5,
    log_enum_key: "INITIALIZE_RULE_SERVICE_comparison",
    title: "Initialize rule service with all parameters.",
    displayMessage: "Initialized rule service with all parameters.",
    is_visible_on_ui: true,
  },
  {
    id: 6,
    log_enum_key: "LOAD_RESOURCES_INFO_comparison",
    title: "Load Resource, column details and Domain Information completed.",
    displayMessage: "Loaded resource, column details, and domain information.",
    is_visible_on_ui: true,
  },
  {
    id: 7,
    log_enum_key: "EXECUTE_PRE_PROCESS_SCRIPTS_comparison",
    title: "Pre-processing Scripts Execution completed.",
    displayMessage: "Pre-processing scripts executed.",
    is_visible_on_ui: true,
  },
  {
    id: 8,
    log_enum_key: "PREPARE_PREFIX_MAPPING_comparison",
    title: "Prefix Mapping Preparation completed.",
    displayMessage: "Prefix mapping prepared.",
    is_visible_on_ui: true,
  },
  {
    id: 9,
    log_enum_key: "PREPARE_FOR_SAME_RESOURCE_comparison",
    title: "Check and prepare steps for same resource completed.",
    displayMessage: "Checked and prepared steps for the same resource.",
    is_visible_on_ui: true,
  },
  {
    id: 10,
    log_enum_key: "PREPARE_RESOURCE_comparison",
    title: "Prepare data for resource id completed.",
    displayMessage: "Prepare data for resource id completed.",
    is_visible_on_ui: true,
  },
  {
    id: 11,
    log_enum_key: "VALIDATE_RESOURCE_comparison",
    title: "Validation execution for resource id completed.",
    displayMessage: "Validation execution for resource id completed.",
    is_visible_on_ui: true,
  },
  {
    id: 12,
    log_enum_key: "PROCESS_PRIMARY_DATASET_STARTED_comparison",
    title: "Load and process Primary Dataset started.",
    displayMessage: "Started loading and processing the primary dataset.",
    is_visible_on_ui: true,
  },
  {
    id: 13,
    log_enum_key: "PROCESS_PRIMARY_DATASET_comparison",
    title: "Load and process Primary Dataset completed.",
    displayMessage: "Loaded and processed the primary dataset.",
    is_visible_on_ui: true,
  },
  {
    id: 14,
    log_enum_key: "PROCESS_SECONDARY_MERGE_RESOURCE_STARTED_comparison",
    title:
      "Process Secondary Merge Resource started for main resource id {resource_id}.",
    displayMessage:
      "Started processing the secondary merge resource for main resource id {resource_id}.",
    unresolvedDisplayMessage:
      "Started processing the secondary merge resource.",
    is_visible_on_ui: true,
  },
  {
    id: 15,
    log_enum_key: "PROCESS_SECONDARY_MERGE_RESOURCE_comparison",
    title:
      "Process secondary merge resource completed for main resource {resource_id}.",
    displayMessage:
      "Processed the secondary merge resource for main resource {resource_id}.",
    unresolvedDisplayMessage: "Processed the secondary merge resource.",
    is_visible_on_ui: true,
  },
  {
    id: 16,
    log_enum_key: "PRIMARY_MERGE_KEYS_comparison",
    title: "Generate primary merge keys dataframe completed.",
    displayMessage: "Generated primary merge keys dataframe.",
    is_visible_on_ui: true,
  },
  {
    id: 17,
    log_enum_key: "PROCESS_SECONDARY_DATASET_STARTED_comparison",
    title:
      "Load and process secondary dataset started for secondary dataset resource id {resource_id}.",
    displayMessage:
      "Started loading and processing the secondary dataset for secondary dataset resource id {resource_id}.",
    is_visible_on_ui: true,
  },
  {
    id: 18,
    log_enum_key: "PROCESS_SECONDARY_DATASET_comparison",
    title:
      "Load and process secondary dataset completed for secondary dataset resource id {resource_id}.",
    displayMessage:
      "Loaded and processed the secondary dataset for secondary dataset resource id {resource_id}.",
    is_visible_on_ui: true,
  },
  {
    id: 19,
    log_enum_key: "SECONDARY_MERGE_KEYS_comparison",
    title: "Generate secondary merge keys dataframe completed.",
    displayMessage: "Generated secondary merge keys dataframe.",
    is_visible_on_ui: true,
  },
  {
    id: 20,
    log_enum_key: "SECONDARY_MERGE_RSC_MERGE_KEYS_comparison",
    title: "Process merge keys df for secondary merge resources.",
    displayMessage:
      "Processed merge keys dataframe for secondary merge resources.",
    is_visible_on_ui: true,
  },
  {
    id: 21,
    log_enum_key: "MERGE_RESOURCES_DATA_comparison",
    title: "Merge resources data and generate merge keys df completed.",
    displayMessage: "Merged resources data and generated merge keys dataframe.",
    is_visible_on_ui: true,
  },
  {
    id: 22,
    log_enum_key: "GENERATE_MISSING_RECORDS_comparison",
    title: "Generate the missing records completed.",
    displayMessage: "Generated the missing records.",
    is_visible_on_ui: true,
  },
  {
    id: 23,
    log_enum_key: "EXECUTE_ADHOC_QUERY_comparison",
    title: "Executing the adhoc queries completed.",
    displayMessage: "Executed the ad-hoc queries.",
    is_visible_on_ui: true,
  },
  {
    id: 24,
    log_enum_key: "GENERATE_COMP_KEYS_comparison",
    title: "Generate comparison keys for rule result.",
    displayMessage: "Generated comparison keys for rule result.",
    is_visible_on_ui: true,
  },
  {
    id: 25,
    log_enum_key: "GENERATE_REPORT_comparison",
    title: "Generate the execution report.",
    displayMessage: "Generated the execution report.",
    is_visible_on_ui: true,
  },
  {
    id: 26,
    log_enum_key: "CONSTRUCT_RULE_RESULT_comparison",
    title: "Construct the rule result object.",
    displayMessage: "Constructed the rule result object.",
    is_visible_on_ui: true,
  },
  {
    id: 27,
    log_enum_key: "EXTRACT_TOLERANCE_COlS_comparison",
    title: "Extract tolerance columns completed.",
    displayMessage: "Extracted tolerance columns.",
    is_visible_on_ui: true,
  },
  {
    id: 28,
    log_enum_key: "EXECUTE_DOMAIN_COMP_comparison",
    title: "Executing domain comparisons completed.",
    displayMessage: "Executed domain comparisons.",
    is_visible_on_ui: true,
  },
  {
    id: 29,
    log_enum_key: "EXECUTE_CUSTOM_COMP_comparison",
    title: "Executing custom comparisons completed.",
    displayMessage: "Executed custom comparisons.",
    is_visible_on_ui: true,
  },
  {
    id: 30,
    log_enum_key: "MISMATCHED_DB_STORAGE_comparison",
    title: "Mismatched records database storage completed.",
    displayMessage: "Stored mismatched records in the database.",
    is_visible_on_ui: true,
  },
  {
    id: 31,
    log_enum_key: "MISMATCHED_ISSUES_CREATE_comparison",
    title: "Mismatched records issue creation completed.",
    displayMessage: "Created issues for mismatched records.",
    is_visible_on_ui: true,
  },
  {
    id: 32,
    log_enum_key: "VALIDATION_PROCESS_STARTED_comparison",
    title:
      "Validation process started for comparison resource with resource id {resource_id}.",
    displayMessage:
      "Validation process started for comparison resource with resource id {resource_id}.",
    is_visible_on_ui: true,
  },
  {
    id: 33,
    log_enum_key: "DOMAIN_TRANSLATION_comparison",
    title: "Apply domain translation completed.",
    displayMessage: "Apply domain translation completed.",
    is_visible_on_ui: true,
  },
  {
    id: 34,
    log_enum_key: "TRANSLATE_REF_DATA_comparison",
    title: "Translate reference data completed.",
    displayMessage: "Translate reference data completed.",
    is_visible_on_ui: true,
  },
  {
    id: 35,
    log_enum_key: "APPLY_FILTER_RULES_comparison",
    title: "Apply rule filter rules completed.",
    displayMessage: "Apply rule filter rules completed.",
    is_visible_on_ui: true,
  },
  {
    id: 36,
    log_enum_key: "MISSING_DB_STORAGE_comparison",
    title: "Missing records database storage completed.",
    displayMessage: "Stored missing records in the database.",
    is_visible_on_ui: true,
  },
  {
    id: 37,
    log_enum_key: "MISSING_ISSUES_CREATE_comparison",
    title: "Missing records issue creation completed.",
    displayMessage: "Created issues for missing records.",
    is_visible_on_ui: true,
  },
  {
    id: 38,
    log_enum_key: "SAVE_EXECUTION_PARAMS_comparison",
    title: "Save rule execution parameters.",
    displayMessage: "Saved rule execution parameters.",
    is_visible_on_ui: true,
  },
  {
    id: 39,
    log_enum_key: "EXECUTE_PARTICULAR_RESEARCH_QUERY_comparison",
    title: "Executing research query {research_query}.",
    displayMessage: "Executing research query {research_query}.",
    is_visible_on_ui: true,
  },
  {
    id: 40,
    log_enum_key: "FETCH_RESEARCH_QUERY_EXTERNAL_RESC_comparison",
    title:
      "Fetching of external resource for comparison research query completed.",
    displayMessage:
      "Fetching of external resource for comparison research query completed.",
    is_visible_on_ui: true,
  },
  {
    id: 41,
    log_enum_key: "EXECUTE_COMPARISON_RESEARCH_QUERY_comparison",
    title: "Executing comparison research query completed.",
    displayMessage: "Executed comparison research query.",
    is_visible_on_ui: true,
  },
  {
    id: 42,
    log_enum_key: "UPDATE_RULE_RESULT_comparison",
    title: "Update sample value for rule result completed.",
    displayMessage: "Updated sample value for rule result.",
    is_visible_on_ui: true,
  },
  {
    id: 43,
    log_enum_key: "COMPARISON_COMPLETED_comparison",
    title: "Rule execution completed.",
    displayMessage: "Rule execution completed.",
    is_visible_on_ui: true,
  },
  {
    id: 44,
    log_enum_key: "CLEANUP_RESOURCES_comparison",
    title: "Cleanup resources and memory.",
    displayMessage: "Cleaned up resources and memory.",
    is_visible_on_ui: true,
  },
];

/**
 * Child execution steps that are excluded from the Comparison History timeline.
 * These are low-level internal steps that add noise without user value (DQ-8829).
 * Matching is done on log_enum_key so it is resilient to log message copy changes.
 */
export const COMPARISON_HISTORY_EXCLUDED_STEP_KEYS: string[] = [
  "PREPARE_RESOURCE_comparison",
  "VALIDATE_RESOURCE_comparison",
  "VALIDATION_PROCESS_STARTED_comparison",
  "DOMAIN_TRANSLATION_comparison",
  "TRANSLATE_REF_DATA_comparison",
  "APPLY_FILTER_RULES_comparison",
  "EXECUTE_PARTICULAR_RESEARCH_QUERY_comparison",
  "FETCH_RESEARCH_QUERY_EXTERNAL_RESC_comparison",
];

/**
 * Comparison timeline steps as rendered on the Comparison History page.
 * Excludes the redundant child steps listed in COMPARISON_HISTORY_EXCLUDED_STEP_KEYS.
 * Defined at module level so the array reference stays stable across renders.
 */
export const COMPARISON_HISTORY_TIMELINE_STEPS: ComparisonTimelineStep[] =
  COMPARISON_TIMELINE_STEPS.filter(
    (step) =>
      !COMPARISON_HISTORY_EXCLUDED_STEP_KEYS.includes(step.log_enum_key),
  );

/**
 * Terminal timeline step per job type.
 * Once this log_enum_key is present in the timeline status response the process
 * has reached its final step and the timeline no longer needs to be polled (DQ-8830).
 * Matching is done on log_enum_key so it is resilient to log message copy changes.
 */
export const TERMINAL_TIMELINE_STEP_KEYS: Record<string, string> = {
  comparison: "CLEANUP_RESOURCES_comparison",
  validation: "CLEANUP_RESOURCES_validation",
};

/**
 * Get timeline steps filtered by visibility
 * @param steps - Timeline steps to filter (defaults to VALIDATION_TIMELINE_STEPS)
 */
export const getVisibleTimelineSteps = (
  steps: ComparisonTimelineStep[] = VALIDATION_TIMELINE_STEPS,
): ComparisonTimelineStep[] => {
  return steps.filter((step) => step.is_visible_on_ui);
};

/**
 * Get timeline step by log enum key
 * @param logEnumKey - The log enum key to search for
 * @param steps - Timeline steps to search in (defaults to VALIDATION_TIMELINE_STEPS)
 */
export const getTimelineStepByKey = (
  logEnumKey: string,
  steps: ComparisonTimelineStep[] = VALIDATION_TIMELINE_STEPS,
): ComparisonTimelineStep | undefined => {
  return steps.find((step) => step.log_enum_key === logEnumKey);
};

/**
 * Get timeline steps by job type (comparison or validation)
 * @param jobType - The job type suffix to filter by
 * @param steps - Timeline steps to filter (defaults to VALIDATION_TIMELINE_STEPS)
 */
export const getTimelineStepsByJobType = (
  jobType: string,
  steps: ComparisonTimelineStep[] = VALIDATION_TIMELINE_STEPS,
): ComparisonTimelineStep[] => {
  const suffix = `_${jobType}`;
  return steps.filter(
    (step) => step.log_enum_key.endsWith(suffix) && step.is_visible_on_ui,
  );
};
